import { createContext, useContext } from 'react';
import type { Region } from './regions';
import type { ApiPageSummary } from './api';
import { makeT } from './i18n';

export interface SiteContextValue {
  region: Region;
  /**
   * Every region the CMS publishes, for the switcher in the header. The list is here
   * rather than fetched again in the header: `App` already has it, and a second request
   * for the same four rows would be one more thing to be out of step with `region`.
   */
  regions: Region[];
  locale: string;
  /** Prefix every internal link with the language, e.g. /pl/kontakt. */
  path: (slug: string) => string;
  /**
   * URL of an article, built from the slug of the page an editor marked as the news
   * index. Never hardcode "aktualnosci": the listing is called something else in every
   * other region.
   */
  articlePath: (articleSlug: string) => string;
  /** Every published page of this region, in every one of its languages. */
  pages: ApiPageSummary[];
  /**
   * Whether `pages` can be trusted to be the whole list yet.
   *
   * It matters because the list is what tells a page URL from an article URL - see
   * `DynamicPage`. An empty array means three different things while it is `loading`, and
   * `failed` is the case where the answer has to be worked out by asking the API instead.
   */
  pagesStatus: 'loading' | 'ready' | 'failed';
  t: ReturnType<typeof makeT>;
}

export const SiteContext = createContext<SiteContextValue | null>(null);

export function useSite(): SiteContextValue {
  const value = useContext(SiteContext);
  if (!value) throw new Error('useSite must be used inside <SiteContext.Provider>');
  return value;
}

/**
 * The site context if there is one, `null` if there is not.
 *
 * `useSite` throws on purpose: a section rendered outside the provider is a bug worth
 * failing loudly on. But a few things render *before* the provider exists - the loading
 * state shown while the region list is still in flight - and they need to ask rather
 * than assume. Use this only there.
 */
export function useSiteOptional(): SiteContextValue | null {
  return useContext(SiteContext);
}
